import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCustomerSelector } from '../../context/CustomerSelectorContext';
import { useAuth } from '../../context/AuthContext';
import { CrudList, CrudStyles } from '../shared/Crud';
import EmployeeForm from './EmployeeForm';
import employeeService from '../../services/employeeService';
import { EditIcon, CheckIcon, XIcon } from '../Layout/icons';
import PageWithCustomerSelector from '../PageWithCustomerSelector';
import { useCrud } from '../../hooks/useCrud';
import PhoneActionMenu from '../shared/PhoneActionMenu';
import CopyableText from '../shared/CopyableText';
import MessageModal from '../shared/MessageModal';

const { Button: CrudButton, EmptyState, FilterGroup, StatusBadge } = CrudStyles;

const UsersIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75M13 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0z" />
    </svg>
);

export default function Employees() {
    const location = useLocation();
    const navigate = useNavigate();
    const { selectedCustomerId, isTransportCompany, selectOwnData } = useCustomerSelector();
    const { user } = useAuth();
    const pendingOpenEmployeeIdRef = useRef(null);
    const [messageModal, setMessageModal] = useState({ open: false, title: 'Aviso', message: '' });

    // Verifica se o usuário tem permissão para criar/editar colaboradores
    const canManageEmployees = React.useMemo(() => {
        const allowedRoles = ['ROLE_TRANSPORT_COMPANY', 'ROLE_LEGAL_PERSON', 'ROLE_HR_EXTERNAL_EMPLOYEE'];
        return user?.roles?.some(role => allowedRoles.includes(role)) || false;
    }, [user?.roles]);

    // Verifica se o usuário tem permissão para ativar/inativar colaboradores
    const canToggleEmployeeStatus = React.useMemo(() => {
        const allowedRoles = ['ROLE_TRANSPORT_COMPANY', 'ROLE_LEGAL_PERSON'];
        return user?.roles?.some(role => allowedRoles.includes(role)) || false;
    }, [user?.roles]);

    // Cria um serviço adaptado que inclui customerId
    const adaptedService = React.useMemo(() => {
        // Para empresas normais, sempre usa o selectedCustomerId (que deve estar definido automaticamente)
        // Para empresas de transporte, só funciona se tiver selectedCustomerId
        if (!selectedCustomerId) {
            return null;
        }
        
        return {
            list: (params) => employeeService.list({ ...params, customerId: selectedCustomerId }),
            get: (id) => employeeService.get(id, selectedCustomerId),
            create: (data) => employeeService.create({ ...data, customerId: selectedCustomerId }),
            update: (id, data) => employeeService.update(id, { ...data, customerId: selectedCustomerId }),
            delete: (id) => employeeService.delete(id, selectedCustomerId),
        };
    }, [selectedCustomerId]);

    // Cria um serviço mock quando não há selectedCustomerId para evitar erros
    const mockService = React.useMemo(() => ({
        list: async () => ({ content: [], page: { totalPages: 0, number: 0, first: true, last: true } }),
        get: async () => ({}),
        create: async () => ({}),
        update: async () => ({}),
        delete: async () => ({}),
    }), []);

    const {
        view,
        data,
        loading,
        pagination,
        handlePageChange,
        sorting,
        handleSort,
        localFilters,
        handleFilterChange,
        handleSearch,
        editingItem,
        handleCreate,
        handleUpdate,
        handleNew,
        handleEdit,
        handleCancel,
        setRefetchTrigger,
        setView,
        setEditingItem,
    } = useCrud(adaptedService || mockService, {
        pageSize: 10,
        defaultSort: 'name,asc',
        defaultFilters: {
            name: '',
            email: '',
            removed: 'false',
        },
    });

    // Ref para rastrear o customerId anterior e detectar mudanças
    const prevCustomerIdRef = React.useRef(selectedCustomerId);

    // Deep-link: abrir colaborador a partir do histórico de status da entrega
    useEffect(() => {
        const openEmployeeId = location.state?.openEmployeeId;
        if (openEmployeeId == null || String(openEmployeeId).trim() === '') return;

        pendingOpenEmployeeIdRef.current = openEmployeeId;
        if (location.state?.manageOwnData && isTransportCompany) {
            selectOwnData();
        }
        navigate(location.pathname, { replace: true, state: {} });
    }, [location.state, location.pathname, isTransportCompany, navigate, selectOwnData]);

    useEffect(() => {
        const pendingId = pendingOpenEmployeeIdRef.current;
        if (pendingId == null || !selectedCustomerId || !adaptedService) return undefined;

        let cancelled = false;
        (async () => {
            try {
                const employee = await employeeService.get(pendingId, selectedCustomerId);
                if (cancelled || !employee) return;
                pendingOpenEmployeeIdRef.current = null;
                handleEdit(employee);
            } catch {
                if (!cancelled) {
                    pendingOpenEmployeeIdRef.current = null;
                    setMessageModal({
                        open: true,
                        title: 'Erro',
                        message: 'Não foi possível abrir o colaborador solicitado.',
                    });
                }
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCustomerId, adaptedService]);
    
    // Carrega dados quando selectedCustomerId muda
    useEffect(() => {
        // Limpa o estado de edição quando muda de empresa
        const customerIdChanged = prevCustomerIdRef.current !== selectedCustomerId;
        
        if (customerIdChanged && isTransportCompany) {
            // Sempre limpa o estado de edição quando muda de empresa (para empresas de transporte)
            // Fecha o formulário e limpa o item selecionado
            setView('list');
            setEditingItem(null);
        }
        
        // Atualiza a referência
        prevCustomerIdRef.current = selectedCustomerId;
        
        if (selectedCustomerId && adaptedService) {
            setRefetchTrigger(prev => prev + 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCustomerId, adaptedService, setRefetchTrigger, isTransportCompany]);

    const handleToggleStatus = async (id, currentStatus) => {
        const action = currentStatus ? 'ativar' : 'inativar';
        if (!window.confirm(`Tem certeza que deseja ${action} este colaborador?`)) return;

        try {
            await employeeService.toggleStatus(id, selectedCustomerId, currentStatus);
            setRefetchTrigger(prev => prev + 1);
        } catch (error) {
            setMessageModal({
                open: true,
                title: 'Erro',
                message: 'Erro ao alterar status do colaborador',
            });
        }
    };

    const columns = [
        { header: 'Nome', accessor: 'name', sortable: true },
        { 
            header: 'Email', 
            accessor: 'email', 
            sortable: true,
            render: (item) => <CopyableText text={item.email} />
        },
        { 
            header: 'Telefone', 
            accessor: 'phoneNumbers',
            render: (item) => {
                const firstPhone = item.phoneNumbers?.[0]?.number || item.phoneNumbers?.[0];
                return <PhoneActionMenu phoneNumber={firstPhone} />;
            }
        },
        { header: 'Cargo', accessor: 'jobTitle', sortable: true },
        {
            header: 'Status',
            accessor: 'removed',
            sortable: false,
            render: (item) => (
                <StatusBadge $active={!item.removed}>
                    {item.removed ? 'Inativo' : 'Ativo'}
                </StatusBadge>
            )
        },
    ];

    const renderActions = (item) => {
        if (!canManageEmployees && !canToggleEmployeeStatus) {
            return null;
        }

        return (
            <>
                {canManageEmployees && (
                    <CrudButton
                        variant="outline"
                        onClick={() => handleEdit(item)}
                        title="Editar"
                    >
                        <EditIcon />
                    </CrudButton>
                )}
                {canToggleEmployeeStatus && (
                    <CrudButton
                        variant={item.removed ? 'primary' : 'danger'}
                        onClick={() => handleToggleStatus(item.id, item.removed)}
                        title={item.removed ? 'Ativar' : 'Inativar'}
                    >
                        {item.removed ? <CheckIcon /> : <XIcon />}
                    </CrudButton>
                )}
            </>
        );
    };

    const renderFilters = (
        <>
            <FilterGroup>
                <label>Nome</label>
                <input
                    name="name"
                    value={localFilters.name}
                    onChange={handleFilterChange}
                    placeholder="Buscar por nome..."
                />
            </FilterGroup>
            <FilterGroup>
                <label>Email</label>
                <input
                    name="email"
                    value={localFilters.email}
                    onChange={handleFilterChange}
                    placeholder="Buscar por email..."
                />
            </FilterGroup>
            <FilterGroup>
                <label>Status</label>
                <select name="removed" value={localFilters.removed} onChange={handleFilterChange}>
                    <option value="true">Inativo</option>
                    <option value="false">Ativo</option>
                </select>
            </FilterGroup>
        </>
    );

    const messageModalEl = messageModal.open ? (
        <MessageModal
            title={messageModal.title}
            message={messageModal.message}
            onClose={() => setMessageModal((m) => ({ ...m, open: false }))}
        />
    ) : null;

    // View de criação/edição
    if (view === 'create' || view === 'edit') {
        return (
            <PageWithCustomerSelector>
                {messageModalEl}
                <EmployeeForm
                    key={`${view}-${selectedCustomerId}-${editingItem?.id || 'new'}`}
                    initialData={editingItem}
                    onSubmit={view === 'create' ? handleCreate : handleUpdate}
                    onCancel={handleCancel}
                    selectedCustomerId={selectedCustomerId}
                />
            </PageWithCustomerSelector>
        );
    }

    // View de listagem
    return (
        <PageWithCustomerSelector>
            {messageModalEl}
            {!selectedCustomerId ? (
                <EmptyState>
                    <UsersIcon />
                    <h3>
                        {isTransportCompany 
                            ? 'Selecione uma opção' 
                            : 'Carregando colaboradores...'}
                    </h3>
                    <p>
                        {isTransportCompany 
                            ? 'Por favor, selecione "Minha Empresa" ou escolha um cliente no seletor acima para gerenciar colaboradores.'
                            : 'Aguarde enquanto carregamos os dados dos colaboradores.'}
                    </p>
                </EmptyState>
            ) : (
                <CrudList
                    title="Colaboradores"
                    onNew={canManageEmployees ? handleNew : undefined}
                    onSearch={handleSearch}
                    filters={renderFilters}
                    columns={columns}
                    data={data}
                    loading={loading}
                    pagination={{
                        ...pagination,
                        onPageChange: handlePageChange,
                    }}
                    sorting={{
                        ...sorting,
                        onSort: handleSort,
                    }}
                    actions={renderActions}
                />
            )}
        </PageWithCustomerSelector>
    );
}
